import { Action } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useJobFunctionByUuidQuery,
  type JobFunctionByUuidParameters,
  type JobFunctionByUuidResponse,
} from "@/common/api/job-function/use-job-function-by-uuid.query";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { BriefcaseIcon, Fingerprint, PencilIcon, UsersIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";

const JOB_FUNCTION_DETAIL_TRACKING_NAME = "job-function";

export function JobFunctionDetailView(): ReactElement {
  return (
    <Can I="read" a="JobFunction">
      <JobFunctionDetailQuery />
    </Can>
  );
}

function JobFunctionDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const jobFunctionQuery = useJobFunctionByUuidQuery(uuid);

  if (jobFunctionQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<JobFunctionByUuidParameters, JobFunctionByUuidResponse>
      uuid={uuid}
      query={useJobFunctionByUuidQuery}
      queryResult={jobFunctionQuery}
      notFoundProps={{
        title: t("masterdata:JobFunctionNotFound"),
        message: t("masterdata:JobFunctionDoesNotExist"),
        description: t("masterdata:PleaseCheckJobFunctionIdOrContactSupport"),
        backTo: "~/masterdata/job-function",
        backToLabel: t("masterdata:BackToJobFunctionOverview"),
      }}
    >
      {jobFunctionQuery.data ? <JobFunctionDetailContent jobFunction={jobFunctionQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function JobFunctionDetailContent({ jobFunction }: { jobFunction: JobFunctionByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const isDeleted = Boolean(jobFunction.deletedAt);

  const actions: DetailAction[] = isDeleted
    ? []
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: { action: Action.Manage, subject: "JobFunction" },
          onClick: () => navigate(`~/masterdata/job-function/${jobFunction.uuid}/update`),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: "users",
      label: t("common:Users"),
      icon: UsersIcon,
      content: () => <UserTable name="job-function-users" body={{ jobFunctions: [jobFunction.uuid] }} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={jobFunction.uuid}
          technicalId={jobFunction.technicalId}
          createdAt={jobFunction.createdAt}
          updatedAt={jobFunction.updatedAt}
          deletedAt={jobFunction.deletedAt}
          identifiers={[{ id: "v1-id", label: t("masterdata:V1ID"), value: jobFunction.v1Id }]}
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={<BriefcaseIcon />}
          title={jobFunction.name}
          badges={
            isDeleted ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                {t("common:Deleted")}
              </Badge>
            ) : undefined
          }
          actions={actions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={JOB_FUNCTION_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
